import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { UsersThree, PaperPlaneTilt, Trash, Clock, Info, DownloadSimple } from "@phosphor-icons/react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";

const ROLES = ["admin", "analyst", "reviewer", "auditor"];

const ROLE_HELP = [
  ["Admin", "Full access — manage billing, team, branding, settings, and all compliance work."],
  ["Analyst", "Create & edit compliance work — standards, risk, evidence, SOPs, and the AI tools."],
  ["Reviewer", "Review, approve and comment on work, and view everything (limited editing)."],
  ["Auditor", "Read-only access to review the workspace and evidence for audits."],
];

const ACTIVITY_VERB = { invite: "invited", remove: "removed", role_change: "changed the role of" };

const relTime = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const RoleInfo = () => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" data-testid="role-info" aria-label="What can each role do?"
                className="text-slate-400 hover:text-slate-600 align-middle">
          <Info size={14} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-xs p-3">
        <p className="font-semibold text-xs mb-2">What each role can do</p>
        <ul className="space-y-1.5">
          {ROLE_HELP.map(([r, d]) => (
            <li key={r} className="text-xs leading-snug">
              <span className="font-semibold">{r}:</span> <span className="text-slate-300">{d}</span>
            </li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Tenant admins manage their own seats: see the roster + invite / remove.
export const TeamSeats = () => {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("analyst");
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState("");
  const [exporting, setExporting] = useState(false);

  const exportActivityCsv = async () => {
    setExporting(true);
    try {
      await downloadBlob("/tenant/activity.csv", "team-activity.csv");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not export activity log");
    } finally { setExporting(false); }
  };

  const load = useCallback(() => {
    api.get("/tenant/seats").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/tenant/activity").then((r) => setActivity(r.data.activity || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      await api.post("/tenant/seats/invite", { email, name, role });
      toast.success(`Invitation sent to ${email}`);
      setEmail(""); setName(""); setRole("analyst");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send invite");
    } finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email} from your organization?`)) return;
    setRemoving(u.id);
    try {
      await api.delete(`/tenant/seats/${u.id}`);
      toast.success(`Removed ${u.email}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove user");
    } finally { setRemoving(""); }
  };

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    try {
      await api.patch(`/tenant/seats/${u.id}`, { role });
      toast.success(`${u.email} is now ${role}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not change role");
      load();
    }
  };

  if (!data) return null; // non-admins get 403 → panel hidden

  return (
    <Card data-testid="seat-panel" className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2"><UsersThree size={18} /> Team &amp; seats</h3>
        <span data-testid="seat-count" className="text-sm text-slate-500">{data.count} member{data.count === 1 ? "" : "s"}</span>
      </div>

      <form onSubmit={invite} className="flex flex-wrap items-end gap-2 mb-5 rounded-lg bg-slate-50 border p-3">
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs text-slate-500">Email</label>
          <Input data-testid="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" />
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="text-xs text-slate-500">Name</label>
          <Input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
        </div>
        <div className="min-w-[130px]">
          <label className="text-xs text-slate-500 flex items-center gap-1">Role <RoleInfo /></label>
          <select data-testid="invite-role" value={role} onChange={(e) => setRole(e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm capitalize">
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <Button data-testid="invite-submit" type="submit" disabled={busy}>
          <span className="flex items-center gap-1.5"><PaperPlaneTilt size={15} /> {busy ? "Sending…" : "Invite"}</span>
        </Button>
      </form>

      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-400">
          <th className="pb-2 font-medium">Member</th><th className="pb-2 font-medium"><span className="inline-flex items-center gap-1">Role <RoleInfo /></span></th><th></th>
        </tr></thead>
        <tbody>
          {data.seats.map((u) => (
            <tr key={u.id} data-testid={`seat-row-${u.id}`} className="border-t border-slate-100">
              <td className="py-2.5">
                <div className="font-medium text-slate-800">{u.name || u.email.split("@")[0]}</div>
                <div className="text-xs text-slate-500">{u.email}
                  {u.is_self && <span className="ml-1 text-slate-400">(you)</span>}
                  {u.invite_pending && <span className="ml-2 inline-flex items-center gap-0.5 text-amber-600"><Clock size={11} /> invite pending</span>}
                </div>
              </td>
              <td className="py-2.5 capitalize text-slate-600">
                {u.is_superadmin ? (
                  <span>{u.role} <span className="text-slate-400">· claro</span></span>
                ) : u.is_self ? (
                  <span>{u.role}</span>
                ) : (
                  <select
                    data-testid={`seat-role-${u.id}`}
                    value={u.role}
                    onChange={(e) => changeRole(u, e.target.value)}
                    className="h-8 rounded-md border border-input bg-background px-2 text-sm capitalize"
                  >
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                )}
              </td>
              <td className="py-2.5 text-right">
                {!u.is_self && !u.is_superadmin && (
                  <button data-testid={`seat-remove-${u.id}`} onClick={() => remove(u)} disabled={removing === u.id}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-40" title="Remove member">
                    <Trash size={16} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {activity.length > 0 && (
        <div className="mt-6 border-t pt-4" data-testid="seat-activity">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Recent activity</div>
            <Button
              data-testid="export-activity-csv-btn"
              onClick={exportActivityCsv}
              disabled={exporting}
              variant="outline"
              size="sm"
              className="rounded-none h-7 text-xs"
            >
              <DownloadSimple size={14} className="mr-1" />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          </div>
          <ul className="space-y-1.5">
            {activity.slice(0, 8).map((a) => (
              <li key={a.id} className="text-xs text-slate-600 flex items-baseline gap-1.5">
                <span className="text-slate-400 tabular-nums shrink-0" title={a.at ? new Date(a.at).toLocaleString() : ""}>{relTime(a.at)}</span>
                <span>
                  <b className="text-slate-700">{a.actor_name || a.actor_email}</b> {ACTIVITY_VERB[a.action] || a.action}{" "}
                  <b className="text-slate-700">{a.target_email}</b>
                  {a.detail ? <span className="text-slate-400"> · {a.detail}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

export default TeamSeats;
